import { SITE_ORIGIN, canonicalUrl } from "@/lib/site-url";
import type { FaqItem } from "@/lib/faq";
import type { PublicAppConfig } from "@/lib/use-app-config";

// schema.org JSON-LD for the public pages.
//
// Structured data is the one metadata format the answer engines parse most
// reliably, and unlike our rendered copy it survives being read by a machine
// that only skims. Everything here is built from values we can actually
// verify — the company identity comes from `config/appConfig` (the same
// source the legal pages render), never from invented figures.
//
// Deliberately NOT emitted: `aggregateRating`. The homepage quotes "4.5★ pe
// Play Store" as marketing copy, but we have no first-party review corpus to
// back a rating snippet, and self-serving rating markup is exactly what earns
// a structured-data manual action. Add it only if it's sourced from a real,
// auditable review set.

type Json = Record<string, unknown>;

// `</script>` inside a JSON string would close the tag early; escaping the
// angle bracket is the standard defence and stays valid JSON.
function serialise(data: Json | Json[]): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

// Rendered inline rather than through <Helmet>.
//
// react-helmet-async 3 delegates to React 19's native metadata hoisting, which
// covers <title>/<meta>/<link> but silently drops a <script> child — the tag
// simply never reaches the document. Google reads JSON-LD from the body just
// as happily as from the head, and rendering it inline has a second benefit:
// it is part of the component's own output, so it appears in server-rendered
// HTML without any extra extraction step.
export function JsonLd({ data }: { data: Json | Json[] }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serialise(data) }}
    />
  );
}

const ORG_ID = `${SITE_ORIGIN}/#organization`;
const SITE_ID = `${SITE_ORIGIN}/#website`;

export function organizationSchema(config: PublicAppConfig): Json {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Asistent Auto",
    legalName: config.companyName,
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/full_icon.svg`,
    email: config.contactEmail,
    taxID: config.companyUniqueNumber,
    address: {
      "@type": "PostalAddress",
      streetAddress: config.address,
      addressCountry: "RO",
    },
    areaServed: { "@type": "Country", name: "România" },
  };
}

export function websiteSchema(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": SITE_ID,
    url: SITE_ORIGIN,
    name: "Asistent Auto",
    inLanguage: "ro-RO",
    publisher: { "@id": ORG_ID },
  };
}

// The app itself. `offers` at price 0 is the machine-readable form of the
// homepage's "Gratuit, fără reclame".
export function softwareApplicationSchema(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Asistent Auto",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Android, Web",
    url: canonicalUrl("/app"),
    inLanguage: "ro-RO",
    publisher: { "@id": ORG_ID },
    offers: { "@type": "Offer", price: "0", priceCurrency: "RON" },
  };
}

export function faqSchema(items: FaqItem[]): Json {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${canonicalUrl("/intrebari")}#faq`,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

export function breadcrumbSchema(trail: { name: string; path: string }[]): Json {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((step, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: step.name,
      item: canonicalUrl(step.path),
    })),
  };
}

export function articleSchema(a: {
  title: string;
  description: string;
  path: string;
}): Json {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.description,
    mainEntityOfPage: canonicalUrl(a.path),
    inLanguage: "ro-RO",
    publisher: { "@id": ORG_ID },
  };
}
